import React, { useState, createContext, useContext, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, ClipboardList, Package, Store, BarChart3, CheckSquare, Wrench, ChevronDown, ChevronUp, KeyRound, Calendar, Component, MapPinHouse, UserCog, XCircle, MapPin, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentRentalPoint } from '@/stores/useCurrentRentalPoint';

// Create context for mobile navigation
interface MobileNavContextType {
  isMobileNavOpen: boolean;
  toggleMobileNav: () => void;
}
const MobileNavContext = createContext<MobileNavContextType | undefined>(undefined);

// Create provider component
interface MobileNavProviderProps {
  children: ReactNode;
}
export const MobileNavProvider = ({
  children
}: MobileNavProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMobileNav = () => setIsOpen(prev => !prev);
  return <MobileNavContext.Provider value={{
    isMobileNavOpen: isOpen,
    toggleMobileNav
  }}>
      {children}
    </MobileNavContext.Provider>;
};

// Create hook to use mobile nav context
export const useMobileNav = () => {
  const context = useContext(MobileNavContext);
  if (context === undefined) {
    throw new Error('useMobileNav must be used within a MobileNavProvider');
  }
  return context;
};

const MobileNav = () => {
  const [rentalSubmenuOpen, setRentalSubmenuOpen] = useState(false);
  const {
    currentRentalPoint
  } = useCurrentRentalPoint();
  const location = useLocation();
  const currentPath = location.pathname;
  const menuItems = [{
    title: 'Главная',
    path: '/main',
    icon: Home
  }, {
    title: 'Клиенты',
    path: '/clients',
    icon: Users
  }, {
    title: 'Заказы',
    path: '/orders',
    icon: ShoppingCart
  }, {
    title: 'Аренда',
    path: '/rentals',
    icon: KeyRound
  }, {
    title: 'Инвентарь',
    path: '/inventory',
    icon: Package
  }, {
    title: 'Магазин',
    path: '/store',
    icon: Store
  }, {
    title: 'Мастерская',
    path: '/workshop',
    icon: Wrench
  }, {
    title: 'Персонал',
    path: '/staff',
    icon: UserCog
  }, {
    title: 'Отчёты',
    path: '/reports',
    icon: BarChart3
  }, {
    title: 'Задачи',
    path: '/tasks',
    icon: CheckSquare
  }];
  const rentalSubItems = [{
    title: 'Календарь',
    path: '/rental-calendar',
    icon: Calendar
  }, {
    title: 'Комплекты',
    path: '/rental-kits',
    icon: Component
  }, {
    title: 'Кросспункт',
    path: '/cross-location',
    icon: MapPinHouse
  }, {
    title: 'Отказы',
    path: '/rental-denials',
    icon: XCircle
  }];
  const toggleRentalSubmenu = () => setRentalSubmenuOpen(!rentalSubmenuOpen);
  
  // Use the context directly from the provider
  const context = useContext(MobileNavContext);
  if (!context) {
    return null; // Don't render anything if context is not available
  }
  
  const { isMobileNavOpen: isOpen, toggleMobileNav } = context;
  
  return (
    <div className="xl:hidden">
      <div className={cn("fixed inset-0 bg-white z-40 transform transition-transform flex flex-col xl:hidden", isOpen ? "translate-x-0" : "-translate-x-full")}>
        {/* Fixed header */}
        <div className="flex-shrink-0 px-6 py-6 mt-16 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-transparent">
              <span className="text-gray-600 font-bold text-lg">A+</span>
            </div>
          </div>
        </div>

        {/* Scrollable navigation */}
        <div className="flex-1 overflow-y-auto">
          <nav className="flex flex-col px-6 pt-6 space-y-1 pb-20">
            {/* Main menu items */}
            {menuItems.map(item => {
            const isActive = currentPath === item.path;
            return <Link key={item.path} to={item.path} className={cn('flex items-center px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors', isActive && 'bg-blue-50 text-blue-600')} onClick={toggleMobileNav}>
                  {React.createElement(item.icon, {
                size: 24,
                className: cn("mr-4 flex-shrink-0", isActive && 'text-blue-600')
              })}
                  <span className="text-sm font-medium">{item.title}</span>
                </Link>;
          })}

            {/* Rental submenu */}
            <div className="relative w-full">
              <button onClick={toggleRentalSubmenu} className={cn('flex items-center justify-between w-full px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors', (currentPath.includes('/rental-') || currentPath.includes('/cross-location')) && 'bg-blue-50 text-blue-600')}>
                <div className="flex items-center">
                  <Calendar size={24} className="mr-4 flex-shrink-0" />
                  <span className="text-sm font-medium">Управление арендой</span>
                </div>
                {rentalSubmenuOpen ? <ChevronUp size={20} className="flex-shrink-0" /> : <ChevronDown size={20} className="flex-shrink-0" />}
              </button>
              
              {rentalSubmenuOpen && <div className="ml-12 space-y-1 mt-2">
                  {rentalSubItems.map(subItem => {
                const isSubActive = currentPath === subItem.path;
                return <Link key={subItem.path} to={subItem.path} className={cn('flex items-center px-3 py-3 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors', isSubActive && 'bg-blue-50 text-blue-600')} onClick={toggleMobileNav}>
                        {React.createElement(subItem.icon, {
                    size: 20,
                    className: cn("mr-3 flex-shrink-0", isSubActive && 'text-blue-600')
                  })}
                        <span className="text-sm font-medium">{subItem.title}</span>
                      </Link>;
              })}
                </div>}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default MobileNav;
